/**
 * ImageCropper - Modal component for cropping images with a circular mask
 * User can pan and zoom the image within a square viewport.
 * Pan/zoom are constrained so the image always covers the full crop area.
 * On confirm, returns the cropped image as a Blob.
 */
export class ImageCropper {
  private modalElement: HTMLElement | null = null;
  private imageElement: HTMLImageElement | null = null;
  private cropSize = 400; // px - square viewport size

  // Pan & zoom state
  private translateX = 0;
  private translateY = 0;
  private scale = 1;
  private naturalW = 0;
  private naturalH = 0;

  // Drag state
  private isDragging = false;
  private dragStartX = 0;
  private dragStartY = 0;
  private dragTranslateStartX = 0;
  private dragTranslateStartY = 0;

  // Pinch zoom state
  private isPinching = false;
  private pinchStartDistance = 0;
  private pinchStartScale = 0;
  private previousPinchCenterX = 0;
  private previousPinchCenterY = 0;

  // Wheel state
  private wheelHandler: ((e: WheelEvent) => void) | null = null;

  /**
   * Open the cropper modal and return a Promise that resolves with the cropped Blob
   * or null if cancelled.
   */
  async open(file: File): Promise<Blob | null> {
    return new Promise((resolve) => {
      this.createModal(file);
      // Store resolve on the modal element for access from event handlers
      (this.modalElement as any)._resolve = resolve;
    });
  }

  /**
   * Create and display the crop modal
   */
  private createModal(file: File): void {
    // Backdrop
    this.modalElement = document.createElement('div');
    this.modalElement.className = 'fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center';
    this.modalElement.style.zIndex = '100';
    this.modalElement.id = 'image-cropper-modal';

    // Content wrapper
    const wrapper = document.createElement('div');
    wrapper.className = 'flex flex-col items-center gap-4';

    // Title
    const title = document.createElement('h3');
    title.className = 'text-white text-lg font-medium';
    title.textContent = 'Retalla la imatge';
    wrapper.appendChild(title);

    // Subtitle
    const subtitle = document.createElement('p');
    subtitle.className = 'text-gray-300 text-sm';
    subtitle.textContent = 'Arrossega per posicionar • Pessiga o desplaça per zoom';
    wrapper.appendChild(subtitle);

    // Crop container (square, overflow hidden) – responsive
    const container = document.createElement('div');
    container.className = 'relative overflow-hidden rounded-lg border-2 border-white/30';
    const size = this.getResponsiveSize();
    this.cropSize = size;
    container.style.width = `${size}px`;
    container.style.height = `${size}px`;
    container.style.background = '#1a1a1a';
    container.style.boxSizing = 'border-box';

    // Image element (hidden until positioned by resetTransform)
    this.imageElement = new Image();
    this.imageElement.style.position = 'absolute';
    this.imageElement.style.transformOrigin = '0 0';
    this.imageElement.style.userSelect = 'none';
    this.imageElement.style.opacity = '0';
    this.imageElement.style.transition = 'opacity 0.15s ease';
    this.imageElement.style.display = 'block';
    // Prevent CSS resets (e.g. Tailwind base) from capping the image layout size.
    // Without this, max-width: 100% on img would shrink the layout box, and then
    // transform: scale() would shrink it further, making the image appear tiny.
    this.imageElement.style.maxWidth = 'none';
    this.imageElement.style.maxHeight = 'none';
    (this.imageElement.style as any).webkitUserDrag = 'none';
    this.imageElement.style.cursor = 'grab';

    this.imageElement.onload = () => {
      this.naturalW = this.imageElement!.naturalWidth;
      this.naturalH = this.imageElement!.naturalHeight;
      this.resetTransform();
      // Fade in once positioned
      requestAnimationFrame(() => {
        if (this.imageElement) this.imageElement.style.opacity = '1';
      });
    };

    this.imageElement.onerror = () => {
      this.close(null);
    };

    // Read file as data URL
    const reader = new FileReader();
    reader.onload = (e) => {
      this.imageElement!.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);

    container.appendChild(this.imageElement);

    // Circular mask overlay (gray outside the circle)
    const overlay = document.createElement('div');
    overlay.className = 'absolute inset-0 pointer-events-none';
    overlay.style.background = `radial-gradient(closest-side circle at center, transparent 100%, rgba(0, 0, 0, 0.65) 100.1%)`;
    container.appendChild(overlay);

    // Circular border indicator
    const circleBorder = document.createElement('div');
    circleBorder.className = 'absolute inset-0 pointer-events-none';
    circleBorder.style.borderRadius = '50%';
    circleBorder.style.border = '2px solid rgba(255, 255, 255, 0.5)';
    circleBorder.style.width = `${this.cropSize}px`;
    circleBorder.style.height = `${this.cropSize}px`;
    container.appendChild(circleBorder);

    wrapper.appendChild(container);

    // Buttons
    const buttonGroup = document.createElement('div');
    buttonGroup.className = 'flex gap-3';

    const cancelButton = document.createElement('button');
    cancelButton.type = 'button';
    cancelButton.className = 'px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-500 font-medium';
    cancelButton.textContent = 'Cancel·la';
    cancelButton.addEventListener('click', () => this.close(null));

    const cropButton = document.createElement('button');
    cropButton.type = 'button';
    cropButton.className = 'px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500 font-medium';
    cropButton.textContent = 'Retalla i utilitza';
    cropButton.addEventListener('click', () => this.performCrop());

    buttonGroup.appendChild(cancelButton);
    buttonGroup.appendChild(cropButton);
    wrapper.appendChild(buttonGroup);

    this.modalElement.appendChild(wrapper);

    // Setup drag interaction on the image
    this.setupDrag(container);

    // Setup wheel zoom
    this.setupWheelZoom(container);

    // Backdrop click to close
    this.modalElement.addEventListener('click', (e) => {
      if (e.target === this.modalElement) {
        this.close(null);
      }
    });

    // Escape key to close
    const escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close(null);
        document.removeEventListener('keydown', escapeHandler);
      }
    };
    document.addEventListener('keydown', escapeHandler);

    document.body.appendChild(this.modalElement);
  }

  /**
   * Calculate min scale that fully covers the crop area
   */
  private getMinScale(): number {
    return Math.max(this.cropSize / this.naturalW, this.cropSize / this.naturalH);
  }

  /**
   * Pick a crop size that fits the viewport (with margins) on mobile,
   * or uses the default 400 px on larger screens.
   */
  private getResponsiveSize(): number {
    const margin = 48; // padding around the modal (top+bottom, buttons, title)
    const maxByWidth = window.innerWidth - 32;
    const maxByHeight = window.innerHeight - margin;
    return Math.min(this.cropSize, maxByWidth, maxByHeight);
  }

  /**
   * Reset image transform to center it in the crop area at minimum scale
   */
  private resetTransform(): void {
    this.scale = this.getMinScale();
    const scaledW = this.naturalW * this.scale;
    const scaledH = this.naturalH * this.scale;
    this.translateX = (this.cropSize - scaledW) / 2;
    this.translateY = (this.cropSize - scaledH) / 2;
    this.applyTransform();
  }

  /**
   * Constrain translation so image always covers the full viewport
   */
  private constrainTranslation(): void {
    const scaledW = this.naturalW * this.scale;
    const scaledH = this.naturalH * this.scale;

    // Image must extend from at most 0 to at least cropSize on both axes
    const minX = this.cropSize - scaledW; // right edge at cropSize
    const maxX = 0; // left edge at 0
    const minY = this.cropSize - scaledH;
    const maxY = 0;

    this.translateX = Math.max(minX, Math.min(maxX, this.translateX));
    this.translateY = Math.max(minY, Math.min(maxY, this.translateY));
  }

  /**
   * Apply the current transform to the image
   */
  private applyTransform(): void {
    if (!this.imageElement) return;
    this.imageElement.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
  }

  /**
   * Euclidean distance between two touch points
   */
  private static touchDistance(e: TouchEvent): number {
    const dx = e.touches[0].clientX - e.touches[1].clientX;
    const dy = e.touches[0].clientY - e.touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Midpoint between two touch points (relative to a container)
   */
  private static touchCenter(e: TouchEvent, container: HTMLElement): { x: number; y: number } {
    const rect = container.getBoundingClientRect();
    return {
      x: (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left,
      y: (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top,
    };
  }

  /**
   * Setup drag (mouse + touch) and pinch-to-zoom interaction
   */
  private setupDrag(container: HTMLElement): void {
    const startDrag = (clientX: number, clientY: number) => {
      this.isDragging = true;
      this.dragStartX = clientX;
      this.dragStartY = clientY;
      this.dragTranslateStartX = this.translateX;
      this.dragTranslateStartY = this.translateY;
      if (this.imageElement) {
        this.imageElement.style.cursor = 'grabbing';
      }
    };

    const moveDrag = (clientX: number, clientY: number) => {
      if (!this.isDragging) return;
      this.translateX = this.dragTranslateStartX + (clientX - this.dragStartX);
      this.translateY = this.dragTranslateStartY + (clientY - this.dragStartY);
      this.constrainTranslation();
      this.applyTransform();
    };

    const endDrag = () => {
      this.isDragging = false;
      if (this.imageElement) {
        this.imageElement.style.cursor = 'grab';
      }
    };

    // Mouse events
    container.addEventListener('mousedown', (e) => {
      e.preventDefault();
      startDrag(e.clientX, e.clientY);
    });

    document.addEventListener('mousemove', (e) => {
      moveDrag(e.clientX, e.clientY);
    });

    document.addEventListener('mouseup', () => {
      endDrag();
    });

    // Touch events (single-finger drag + two-finger pinch zoom)
    container.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        this.isPinching = true;
        this.isDragging = false;
        this.pinchStartDistance = ImageCropper.touchDistance(e);
        this.pinchStartScale = this.scale;
        // Record initial pinch center for first-frame pan tracking
        const center = ImageCropper.touchCenter(e, container);
        this.previousPinchCenterX = center.x;
        this.previousPinchCenterY = center.y;
      } else if (e.touches.length === 1 && !this.isPinching) {
        e.preventDefault();
        startDrag(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, { passive: false });

    document.addEventListener('touchmove', (e) => {
      if (this.isPinching && e.touches.length === 2) {
        e.preventDefault();

        const dist = ImageCropper.touchDistance(e);
        const minScale = this.getMinScale();
        const newScale = Math.max(minScale, Math.min(20, this.pinchStartScale * (dist / this.pinchStartDistance)));

        // Current finger midpoint
        const center = ImageCropper.touchCenter(e, container);

        // 1. Pan: move the image by the same amount the midpoint moved
        this.translateX += center.x - this.previousPinchCenterX;
        this.translateY += center.y - this.previousPinchCenterY;

        // 2. Zoom toward the current midpoint (keeps the point under fingers anchored)
        const zoomRatio = newScale / this.scale;
        this.translateX = center.x - zoomRatio * (center.x - this.translateX);
        this.translateY = center.y - zoomRatio * (center.y - this.translateY);
        this.scale = newScale;

        // Remember center for next frame's pan delta
        this.previousPinchCenterX = center.x;
        this.previousPinchCenterY = center.y;

        this.constrainTranslation();
        this.applyTransform();
      } else if (this.isDragging && e.touches.length === 1) {
        e.preventDefault();
        moveDrag(e.touches[0].clientX, e.touches[0].clientY);
      }
    }, { passive: false });

    document.addEventListener('touchend', (e) => {
      if (this.isPinching && e.touches.length < 2) {
        this.isPinching = false;
      }
      if (!this.isPinching) {
        endDrag();
      }
    });
  }

  /**
   * Setup wheel zoom with focus on cursor position
   */
  private setupWheelZoom(container: HTMLElement): void {
    this.wheelHandler = (e: WheelEvent) => {
      e.preventDefault();

      if (!this.imageElement) return;

      const rect = container.getBoundingClientRect();
      const cursorX = e.clientX - rect.left;
      const cursorY = e.clientY - rect.top;

      const zoomFactor = e.deltaY < 0 ? 1.1 : 0.9;
      const minScale = this.getMinScale();
      const newScale = Math.max(minScale, Math.min(20, this.scale * zoomFactor));

      // Adjust translate to zoom toward cursor
      const scaleRatio = newScale / this.scale;
      this.translateX = cursorX - scaleRatio * (cursorX - this.translateX);
      this.translateY = cursorY - scaleRatio * (cursorY - this.translateY);
      this.scale = newScale;

      this.constrainTranslation();
      this.applyTransform();
    };

    container.addEventListener('wheel', this.wheelHandler, { passive: false });
  }

  /**
   * Extract the cropped area as a Blob
   */
  private async performCrop(): Promise<void> {
    if (!this.imageElement || !this.imageElement.src) {
      this.close(null);
      return;
    }

    const img = this.imageElement;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      this.close(null);
      return;
    }

    // Output: square image matching the viewport
    canvas.width = this.cropSize;
    canvas.height = this.cropSize;

    // Fill background white (in case of any edge cases)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, this.cropSize, this.cropSize);

    // Convert from displayed DOM image coords to natural image pixel coords
    const scaleX = img.naturalWidth / img.width;
    const scaleY = img.naturalHeight / img.height;

    const offsetX = -this.translateX * scaleX / this.scale;
    const offsetY = -this.translateY * scaleY / this.scale;
    const drawW = this.cropSize * scaleX / this.scale;
    const drawH = this.cropSize * scaleY / this.scale;

    ctx.drawImage(img, offsetX, offsetY, drawW, drawH, 0, 0, this.cropSize, this.cropSize);

    // Export as JPEG with high quality
    canvas.toBlob(
      (blob) => {
        if (blob) {
          this.close(blob);
        } else {
          this.close(null);
        }
      },
      'image/jpeg',
      0.92
    );
  }

  /**
   * Close the modal and resolve the promise
   */
  private close(result: Blob | null): void {
    const modal = this.modalElement;
    const resolve = modal ? (modal as any)._resolve : null;

    if (this.wheelHandler && modal) {
      modal.removeEventListener('wheel', this.wheelHandler);
    }

    if (modal && modal.parentNode) {
      modal.parentNode.removeChild(modal);
    }
    this.modalElement = null;
    this.imageElement = null;

    if (resolve) {
      resolve(result);
    }
  }
}
